import { useState, useEffect } from 'react'
import {
  Building2,
  Loader2,
  CheckCircle,
  AlertCircle,
  Mail,
  Target,
  Sparkles,
  Globe,
} from 'lucide-react'
import {
  organizationApi,
  BUSINESS_CATEGORY_LABELS,
} from '@/services/organization-api'
import type { IdealCustomerProfile } from '@/services/organization-api'
import { useOrgStore } from '@/stores/org-store'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { TagInput } from '@/components/ui/tag-input'
import { MultiSelect } from '@/components/ui/multi-select'
import { CountryRegionSelect } from '@/components/ui/country-region-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TARGET_MARKET_OPTIONS } from '@/constants/target-markets'

export default function OrgSettingsPage() {
  const org = useOrgStore((s) => s.currentOrg)
  const setCurrentOrg = useOrgStore((s) => s.setCurrentOrg)
  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [industry, setIndustry] = useState('')
  const [businessCategory, setBusinessCategory] = useState<string>('')
  const [targetMarkets, setTargetMarkets] = useState<string[]>([])
  const [targetCountries, setTargetCountries] = useState<string[]>([])
  const [prospectingGoals, setProspectingGoals] = useState('')
  const [outreachFromEmail, setOutreachFromEmail] = useState('')
  const [outreachCtaLink, setOutreachCtaLink] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [savingSection, setSavingSection] = useState<'profile' | 'outreach' | 'icp' | null>(null)
  // ICP state
  const [icp, setIcp] = useState<IdealCustomerProfile>({
    services_offered: [],
    target_customer_industries: [],
    target_company_types: [],
    target_company_size: [],
    value_proposition: '',
    buyer_persona: [],
    geographic_focus: [],
  })
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [activeTab, setActiveTab] = useState('profile')

  // Fetch full organization details on mount
  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!org?.slug) return
      
      setIsLoading(true)
      try {
        const orgDetails = await organizationApi.getBySlug(org.slug)
        setName(orgDetails.name || '')
        setWebsite(orgDetails.website || '')
        setIndustry(orgDetails.industry || '')
        setBusinessCategory(orgDetails.business_category || '')
        setTargetMarkets(orgDetails.target_markets || [])
        setTargetCountries(orgDetails.target_countries || [])
        setProspectingGoals((orgDetails.prospecting_goals || []).join(', '))
        setOutreachFromEmail(orgDetails.outreach_from_email || '')
        setOutreachCtaLink(orgDetails.outreach_call_to_action_link || '')
        // Set ICP if available
        if (orgDetails.ideal_customer_profile) {
          setIcp({
            services_offered: orgDetails.ideal_customer_profile.services_offered || [],
            target_customer_industries: orgDetails.ideal_customer_profile.target_customer_industries || [],
            target_company_types: orgDetails.ideal_customer_profile.target_company_types || [],
            target_company_size: orgDetails.ideal_customer_profile.target_company_size || [],
            value_proposition: orgDetails.ideal_customer_profile.value_proposition || '',
            buyer_persona: orgDetails.ideal_customer_profile.buyer_persona || [],
            geographic_focus: orgDetails.ideal_customer_profile.geographic_focus || [],
          })
        }
      } catch (error) {
        console.error('Error fetching organization details:', error)
        // Fallback to store data if fetch fails
        if (org) {
          setName(org.name || '')
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchOrgDetails()
  }, [org?.slug])


  const save = async (section: 'profile' | 'outreach') => {
    if (!org) return

    setSavingSection(section)
    try {
      const updated = await organizationApi.update(org.id, {
        name,
        website,
        industry,
        business_category: businessCategory || undefined,
        target_markets: targetMarkets,
        target_countries: targetCountries,
        prospecting_goals: prospectingGoals.split(',').map(g => g.trim()).filter(Boolean),
        outreach_from_email: outreachFromEmail.trim() || undefined,
        outreach_call_to_action_link: outreachCtaLink.trim() || undefined,
      })
      
      // Sync local state from server response so form reflects persisted data
      setName(updated.name || '')
      setWebsite(updated.website || '')
      setIndustry(updated.industry || '')
      setBusinessCategory(updated.business_category || '')
      setTargetMarkets(updated.target_markets || [])
      setTargetCountries(updated.target_countries || [])
      setProspectingGoals((updated.prospecting_goals || []).join(', '))
      setOutreachFromEmail(updated.outreach_from_email || '')
      setOutreachCtaLink(updated.outreach_call_to_action_link || '')
      
      // Update the org store with changed fields
      if (updated.name !== org.name || updated.business_category !== org.business_category) {
        await setCurrentOrg({
          ...org,
          name: updated.name ?? org.name,
          business_category: updated.business_category ?? org.business_category,
        })
      }
      
      setMessage({ type: 'success', text: 'Settings saved successfully' })
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving settings' })
      setTimeout(() => setMessage(null), 3000)
    } finally {
      setSavingSection(null)
    }
  }


  const analyzeWebsite = async () => {
    if (!org) return
    
    setIsAnalyzing(true)
    try {
      const result = await organizationApi.analyzeWebsite(org.id)
      if (result.success && result.icp) {
        setIcp({
          services_offered: result.icp.services_offered || [],
          target_customer_industries: result.icp.target_customer_industries || [],
          target_company_types: result.icp.target_company_types || [],
          target_company_size: result.icp.target_company_size || [],
          value_proposition: result.icp.value_proposition || '',
          buyer_persona: result.icp.buyer_persona || [],
          geographic_focus: result.icp.geographic_focus || [],
        })
        setMessage({ type: 'success', text: 'Website analyzed successfully!' })
      } else {
        setMessage({ type: 'error', text: result.error || 'Analysis failed' })
      }
      setTimeout(() => setMessage(null), 5000)
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to analyze website' })
      setTimeout(() => setMessage(null), 5000)
    } finally {
      setIsAnalyzing(false)
    }
  }

  const saveIcp = async () => {
    if (!org) return

    setSavingSection('icp')
    try {
      await organizationApi.updateICP(org.id, icp)
      setMessage({ type: 'success', text: 'ICP saved successfully' })
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving ICP' })
      setTimeout(() => setMessage(null), 3000)
    } finally {
      setSavingSection(null)
    }
  }

  // Helper to update ICP fields
  const updateIcpField = <K extends keyof IdealCustomerProfile>(
    field: K,
    value: IdealCustomerProfile[K]
  ) => {
    setIcp((prev) => ({ ...prev, [field]: value }))
  }

  // Convert comma-separated string to array for tag inputs
  const arrayToTagString = (arr: string[]) => arr.join(', ')
  const tagStringToArray = (str: string) => 
    str.split(',').map(s => s.trim()).filter(Boolean)

  if (!org) {
    return (
      <Alert>
        <AlertDescription>Select an organization first.</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Organization Settings</h1>
        <p className="mt-1 text-muted-foreground">
          Manage your organization's profile and settings
        </p>
      </div>

      {message && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="profile" className="gap-2">
            <Building2 className="h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="outreach" className="gap-2">
            <Mail className="h-4 w-4" />
            Outreach
          </TabsTrigger>
          <TabsTrigger value="icp" className="gap-2">
            <Target className="h-4 w-4" />
            Target Market
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organization Profile
          </CardTitle>
          <CardDescription>
            Update your organization's basic information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name</Label>
                <Input
                  id="name"
                  placeholder="Enter organization name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={savingSection !== null}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://example.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  disabled={savingSection !== null}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="business-category">Business Category</Label>
                <div className="flex items-center gap-2">
                  {businessCategory && (
                    <Badge variant="secondary" className="font-normal">
                      {BUSINESS_CATEGORY_LABELS[businessCategory] ?? businessCategory}
                    </Badge>
                  )}
                  <Select
                    value={businessCategory || 'other'}
                    onValueChange={setBusinessCategory}
                    disabled={savingSection !== null}
                  >
                    <SelectTrigger id="business-category" className="max-w-xs">
                      <SelectValue placeholder="Auto-classified" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(BUSINESS_CATEGORY_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-sm text-muted-foreground">
                  Auto-classified during onboarding. Override if needed. Affects source discovery and search term generation.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry & Company Activities</Label>
                <TagInput
                  id="industry"
                  placeholder="e.g., B2B, fertilizer trading, agriculture, manufacturing"
                  value={industry}
                  onChange={setIndustry}
                  disabled={savingSection !== null}
                  variant="info"
                />
                <p className="text-sm text-muted-foreground">
                  Add tags that describe your industry and business activities. These will be used to find potential business partners and prospects that match your profile.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="targetMarkets">Target Markets</Label>
                <MultiSelect
                  id="targetMarkets"
                  options={TARGET_MARKET_OPTIONS}
                  value={targetMarkets}
                  onChange={setTargetMarkets}
                  placeholder="Select target markets"
                  searchPlaceholder="Search markets..."
                  disabled={savingSection !== null}
                />
                <p className="text-sm text-muted-foreground">
                  Select the market segments you target. This helps improve prospect matching and outreach relevance.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="targetCountries">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    Target Countries
                  </div>
                </Label>
                <CountryRegionSelect
                  id="targetCountries"
                  value={targetCountries}
                  onChange={setTargetCountries}
                  placeholder="Select countries or regions"
                  disabled={savingSection !== null}
                />
                <p className="text-sm text-muted-foreground">
                  Select countries or regions where your target customers are located. This guides source discovery and search term generation.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="prospectingGoals">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    Prospecting Goals
                  </div>
                </Label>
                <TagInput
                  id="prospectingGoals"
                  placeholder="e.g., new buyers, agricultural distributors, enterprise customers"
                  value={prospectingGoals}
                  onChange={setProspectingGoals}
                  disabled={savingSection !== null}
                  variant="success"
                />
                <p className="text-sm text-muted-foreground">
                  What types of leads, customers, or partners are you looking for? (e.g., "new buyers for our fertilizer products", "companies needing accounting services")
                </p>
              </div>

              <Button
                onClick={() => save('profile')}
                disabled={savingSection !== null}
              >
                {savingSection === 'profile' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="outreach">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Outreach Settings
          </CardTitle>
          <CardDescription>
            Sender email and call-to-action link for outreach emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="outreach-from">From Email</Label>
            <Input
              id="outreach-from"
              type="text"
              placeholder="Your Name <you@yourdomain.com>"
              value={outreachFromEmail}
              onChange={(e) => setOutreachFromEmail(e.target.value)}
              disabled={savingSection !== null}
            />
            <p className="text-sm text-muted-foreground">
              Sender for outreach emails. Use format: Name &lt;email@domain.com&gt;
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="outreach-cta">Call-to-Action Link</Label>
            <Input
              id="outreach-cta"
              type="url"
              placeholder="https://calendly.com/yourcompany/demo"
              value={outreachCtaLink}
              onChange={(e) => setOutreachCtaLink(e.target.value)}
              disabled={savingSection !== null}
            />
            <p className="text-sm text-muted-foreground">
              Default demo or scheduling URL for outreach emails
            </p>
          </div>
          <Button
            onClick={() => save('outreach')}
            disabled={savingSection !== null}
          >
            {savingSection === 'outreach' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="icp">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Target Market & Customer Profile
          </CardTitle>
          <CardDescription>
            Define who your ideal customers are. This data is used to find relevant data sources where your potential customers are listed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 mb-4">
            <Button
              variant="outline"
              onClick={analyzeWebsite}
              disabled={isAnalyzing || !website}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Analyze Website
                </>
              )}
            </Button>
            {!website && (
              <p className="text-sm text-muted-foreground self-center">
                Add a website URL above to enable analysis
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="services-offered">Services / Products You Offer</Label>
            <TagInput
              id="services-offered"
              placeholder="e.g., project management software, resource scheduling, bid management"
              value={arrayToTagString(icp.services_offered)}
              onChange={(val) => updateIcpField('services_offered', tagStringToArray(val))}
              disabled={savingSection !== null}
              variant="info"
            />
            <p className="text-sm text-muted-foreground">
              What products or services do you sell?
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="target-industries">Target Customer Industries</Label>
            <TagInput
              id="target-industries"
              placeholder="e.g., construction, engineering, architecture"
              value={arrayToTagString(icp.target_customer_industries)}
              onChange={(val) => updateIcpField('target_customer_industries', tagStringToArray(val))}
              disabled={savingSection !== null}
              variant="info"
            />
            <p className="text-sm text-muted-foreground">
              What industries are your customers in? (Not your industry)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="target-company-types">Target Company Types</Label>
            <TagInput
              id="target-company-types"
              placeholder="e.g., general contractors, engineering firms, construction companies"
              value={arrayToTagString(icp.target_company_types)}
              onChange={(val) => updateIcpField('target_company_types', tagStringToArray(val))}
              disabled={savingSection !== null}
              variant="info"
            />
            <p className="text-sm text-muted-foreground">
              What types of companies buy from you?
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="target-company-size">Target Company Size</Label>
            <TagInput
              id="target-company-size"
              placeholder="e.g., 11-50, 51-200, 201-500"
              value={arrayToTagString(icp.target_company_size)}
              onChange={(val) => updateIcpField('target_company_size', tagStringToArray(val))}
              disabled={savingSection !== null}
              variant="info"
            />
            <p className="text-sm text-muted-foreground">
              What size companies do you target? Use ranges: 1-10, 11-50, 51-200, 201-500, 500+
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="value-proposition">Value Proposition</Label>
            <Input
              id="value-proposition"
              placeholder="e.g., Streamlines project scheduling for construction teams"
              value={icp.value_proposition}
              onChange={(e) => updateIcpField('value_proposition', e.target.value)}
              disabled={savingSection !== null}
            />
            <p className="text-sm text-muted-foreground">
              What problem do you solve for your customers?
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="buyer-persona">Buyer Persona / Decision Makers</Label>
            <TagInput
              id="buyer-persona"
              placeholder="e.g., project managers, operations directors, CTOs"
              value={arrayToTagString(icp.buyer_persona)}
              onChange={(val) => updateIcpField('buyer_persona', tagStringToArray(val))}
              disabled={savingSection !== null}
              variant="info"
            />
            <p className="text-sm text-muted-foreground">
              Job titles that typically make buying decisions
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="geographic-focus">Geographic Focus</Label>
            <TagInput
              id="geographic-focus"
              placeholder="e.g., north_america, europe, global"
              value={arrayToTagString(icp.geographic_focus)}
              onChange={(val) => updateIcpField('geographic_focus', tagStringToArray(val))}
              disabled={savingSection !== null}
              variant="info"
            />
            <p className="text-sm text-muted-foreground">
              Regions you focus on: global, north_america, europe, asia_pacific, latin_america, middle_east, africa
            </p>
          </div>

          <Button onClick={saveIcp} disabled={savingSection !== null}>
            {savingSection === 'icp' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save ICP'
            )}
          </Button>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
